import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { AlertBadge } from "@/components/symin/AlertBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crosshair, ArrowSquareOut, CaretDown, Sparkle, Target, Mountains, StackSimple, CircleNotch } from "@phosphor-icons/react";

const PRIO_COLOR = { "Priority 1": "#C20511", "Priority 2": "#FBBF24", "Priority 3": "#94A3B8" };

function TargetCard({ t, i, pid }) {
  const [open, setOpen] = useState(i === 0);
  const [rat, setRat] = useState(t.rationale || null);
  const [busy, setBusy] = useState(false);
  const pc = PRIO_COLOR[t.priority] || "#94A3B8";

  const genRationale = async () => {
    setBusy(true);
    try { const r = await api.post(`/projects/${pid}/ai/target-rationale`, { area_id: t.area, target_id: t.id }); setRat(r.data.rationale); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg bg-card border border-border overflow-hidden min-w-0" data-testid={`gen-target-${i}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full min-w-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 hover:bg-accent/40 transition-colors text-left">
        <span className="font-mono text-xs text-muted-foreground w-5 sm:w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
        <span className="font-mono font-semibold text-sm text-foreground w-14 sm:w-16 shrink-0 truncate">{t.label}</span>
        {t.drill_ready && <span className="hidden sm:inline-flex"><AlertBadge level="green" testid={`gen-drillready-${i}`}>Drill-Ready</AlertBadge></span>}
        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] shrink-0" style={{ color: pc, background: `${pc}1F`, border: `1px solid ${pc}59` }}>{(t.priority || "-").replace("Priority ", "P")}</span>
        <span className="hidden md:block text-xs text-muted-foreground flex-1 truncate">{t.commodity}{t.deposit_model ? ` · ${t.deposit_model}` : ""} · {t.area_name}</span>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <div className="hidden sm:block w-24 h-1.5 rounded-full bg-border overflow-hidden"><div className="h-full rounded-full" style={{ width: `${t.prospectivity}%`, background: "#C20511" }} /></div>
          <span className="font-mono font-semibold text-sm w-10 text-right" style={{ color: "#C20511" }}>{t.prospectivity}</span>
          <CaretDown size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-accent/20 border-t border-border/60" data-testid={`gen-why-${i}`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">Evidence Contribution</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            {t.contributions.map((c) => (
              <div key={c.label}>
                <div className="flex justify-between text-[11px] mb-1"><span className="text-muted-foreground">{c.label}</span><span className="font-mono" style={{ color: c.color }}>{c.value}%</span></div>
                <div className="h-1.5 rounded-full bg-border overflow-hidden"><div className="h-full rounded-full" style={{ width: `${c.value}%`, background: c.color }} /></div>
              </div>
            ))}
          </div>
          {rat && <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap mb-2" data-testid={`gen-rationale-${i}`}>{rat}</div>}
          <Button size="sm" variant={rat ? "ghost" : "outline"} className="h-7 text-xs" onClick={genRationale} disabled={busy} data-testid={`gen-rationale-btn-${i}`}>
            {busy ? <><CircleNotch size={13} className="animate-spin mr-1.5" /> Generating…</> : <><Sparkle size={13} className="mr-1.5" style={{ color: "#A855F7" }} /> {rat ? "Regenerate with AI" : "Generate AI rationale"}</>}
          </Button>
          <div className="mt-2"><AlertBadge level="gold">Conceptual · Field Verification Required</AlertBadge></div>
        </div>
      )}
    </div>
  );
}

function ModelBlock({ title, icon, items, color }) {
  if (!items || !items.length) return null;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1.5" style={{ color }}>{icon}{title}</div>
      <ul className="space-y-1">
        {items.map((x, i) => <li key={i} className="text-xs text-muted-foreground leading-snug flex gap-2"><span style={{ color }}>•</span>{x}</li>)}
      </ul>
    </div>
  );
}

export default function TargetGeneration() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await api.get(`/projects/${id}/target-generation`); setData(r.data); }
      catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const models = data?.models || {};
  const c = data?.counts || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="targets" />
      <div className="ml-[64px] flex flex-col min-h-screen">
        <header className="h-[52px] shrink-0 sticky top-0 z-[900] bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <Crosshair size={18} style={{ color: "#C20511" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="targetgen-title">Target Generation</h1>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Badge className="text-[10px] border-transparent whitespace-nowrap" style={{ background: "#10B98122", color: "#10B981" }} data-testid="targetgen-drillready">{c.drill_ready || 0} drill-ready</Badge>
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="targetgen-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        {loading ? (
          <div className="p-6 space-y-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-card border border-border animate-pulse" />)}</div>
        ) : (
          <div className="max-w-[1640px] mx-auto w-full px-3 sm:px-5 py-5 grid lg:grid-cols-3 gap-5">
            {/* Ranked targets */}
            <div className="lg:col-span-2 space-y-3 min-w-0" data-testid="targetgen-list">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <h2 className="font-display font-semibold text-base truncate min-w-0">Ranked drill targets</h2>
                <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">{c.total || 0} targets · {c.priority1 || 0} P1</span>
              </div>
              {(data?.targets || []).length === 0
                ? <div className="rounded-lg bg-card border border-border px-4 py-10 text-center text-sm text-muted-foreground">No targets yet - process the licence areas to generate exploration targets.</div>
                : (data?.targets || []).map((t, i) => <TargetCard key={t.id || i} t={t} i={i} pid={id} />)}
            </div>

            {/* Mineral system models */}
            <div className="space-y-4 min-w-0" data-testid="targetgen-models">
              <h2 className="font-display font-semibold text-base">Mineral system model</h2>
              {Object.entries(models).map(([comm, m]) => (
                <div key={comm} className="rounded-lg bg-card border border-border p-4" data-testid={`model-${comm}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Target size={16} style={{ color: "#C20511" }} />
                    <span className="font-display font-semibold text-sm">{m.label}</span>
                    {m.group && <Badge variant="outline" className="text-[9px]">{m.group}</Badge>}
                  </div>
                  {m.report_intro && <p className="text-xs text-muted-foreground leading-relaxed mb-3">{m.report_intro}</p>}
                  <ModelBlock title="Deposit models" icon={<Mountains size={13} />} items={m.deposit_models} color="#E67332" />
                  <ModelBlock title="Structural controls" icon={<Crosshair size={13} />} items={m.structural_controls} color="#38BDF8" />
                  <ModelBlock title="Alteration indicators" icon={<Sparkle size={13} />} items={m.alteration_indicators} color="#A855F7" />
                  <ModelBlock title="Lithological / geomorphic" icon={<StackSimple size={13} />} items={m.litho_controls} color="#10B981" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
